<script>
/**
 * A customer group is managed from one screen: what is in it, and who can see
 * it. Keeping those two together is the point — adding a customer here makes it
 * immediately obvious which colleagues just gained access to them, which is
 * impossible to judge if membership and access live on separate pages.
 */
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { CLIENT_GROUP_MODULE } from "@/state/modules/clientGroup";
import { CLIENT_MODULE } from "@/state/modules/client";
import {
  clientGroupMethods,
  clientMethods,
  leadMethods,
  contactMethods,
  opportunityMethods,
  usersMethods,
} from "@/state/helpers";
import crmFormMixin from "@/mixins/crmForm.mixin";
import { getDebtors as getDebtorsDirect } from "@/helpers/services/lookup.service";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

const PRESET_COLORS = ["#7C4DFF", "#3D5AFE", "#00BCD4", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#8B5CF6"];

export default {
  page() {
    return { title: this.isEdit ? i18n.t("clientGroup.edit") : i18n.t("clientGroup.create") };
  },
  components: { Layout, PageHeader, Multiselect },
  mixins: [crmFormMixin],
  data() {
    return {
      form: {
        name: "",
        description: "",
        color: "#7C4DFF",
        debtor_codes: [],
        lead_uuids: [],
        contact_uuids: [],
        opportunity_uuids: [],
        user_uuids: [],
      },
      selected: {
        debtors: [],
        leads: [],
        contacts: [],
        opportunities: [],
        users: [],
      },
      presetColors: PRESET_COLORS,
      loadingOptions: true,
      // The customer picker searches the ledger as you type. Null means it has
      // not been searched yet and the opening list is what shows; a search
      // replaces it. It cannot filter locally like the other three pickers do:
      // there are more customers than the opening page holds, so the ones it
      // does not hold could never be added to a group.
      debtorSearchResults: null,
      debtorSearching: false,
      debtorSearchTimer: null,
    };
  },
  validations: {
    form: { name: { required } },
  },
  computed: {
    ...mapState(CLIENT_GROUP_MODULE, ["details"]),
    ...mapState(CLIENT_MODULE, { clientList: "list" }),
    isEdit() {
      return !!this.$route.params.id;
    },
    title() {
      return this.isEdit ? i18n.t("clientGroup.edit") : i18n.t("clientGroup.create");
    },
    items() {
      return [
        { text: i18n.t("module.customer"), href: "/" },
        { text: i18n.t("clientGroup.title"), href: "/client-groups" },
        { text: this.title, active: true },
      ];
    },
    debtorOptions() {
      const rows = this.debtorSearchResults ?? (this.clientList?.data || []);
      const options = rows
        .map((c) => ({
          key: c.accountNo || c.account_no,
          label: `${c.accountNo || c.account_no} — ${c.companyName || c.company_name || ""}`,
        }))
        .filter((c) => c.key);

      // Members already in the group stay listed whatever the search returned,
      // or clearing the box would drop them from the group on the next save
      // without anyone seeing it happen.
      const shown = new Set(options.map((o) => o.key));
      return [...(this.selected.debtors || []).filter((o) => !shown.has(o.key)), ...options];
    },
    leadOptions() {
      // Members already in the group stay listed even once converted, or the
      // next save would drop them from the group without anyone noticing.
      const linked = this.form.lead_uuids || [];
      return (this.$store.state.lead.list || [])
        .filter((l) => l.status !== "converted" || linked.includes(l.uuid))
        .map((l) => ({
        key: l.uuid,
        label: l.company_name || l.contact_person || l.phone,
      }));
    },
    contactOptions() {
      return (this.$store.state.contact.list || []).map((c) => ({
        key: c.uuid,
        label: c.full_name + (c.company_name ? ` — ${c.company_name}` : ""),
      }));
    },
    opportunityOptions() {
      return (this.$store.state.opportunity.list || []).map((o) => ({
        key: o.uuid,
        label: o.title,
      }));
    },
    userOptions() {
      return (this.$store.state.users.list.data || []).map((u) => ({
        key: u.id,
        label: u.name + (u.agentCode ? ` (${u.agentCode})` : ""),
      }));
    },
    /** Drives the member sections so the markup stays one loop. */
    memberSections() {
      return [
        { key: "debtors", field: "debtor_codes", label: this.$t("module.client"), icon: "mdi-account-multiple", options: this.debtorOptions, onSearch: this.onDebtorSearch, loading: this.debtorSearching },
        { key: "leads", field: "lead_uuids", label: this.$t("lead.title"), icon: "mdi-account-search-outline", options: this.leadOptions },
        { key: "contacts", field: "contact_uuids", label: this.$t("contact.title"), icon: "mdi-card-account-phone-outline", options: this.contactOptions },
        { key: "opportunities", field: "opportunity_uuids", label: this.$t("opportunity.title"), icon: "mdi-handshake-outline", options: this.opportunityOptions },
      ];
    },
    totalRecords() {
      return (
        this.form.debtor_codes.length +
        this.form.lead_uuids.length +
        this.form.contact_uuids.length +
        this.form.opportunity_uuids.length
      );
    },
  },
  async created() {
    // Everything selectable, loaded once — the pickers filter locally after this.
    await Promise.all([
      this.getAllClients({ filter: { pageNumber: 1, pageSize: 1000 } }).catch(() => {}),
      this.getAllLeads({ filter: { page: 1, size: 1000 } }).catch(() => {}),
      this.getContacts({ filter: { page: 1, size: 1000 } }).catch(() => {}),
      this.getAllOpportunities({ filter: { size: 1000 } }).catch(() => {}),
      this.getAllUser({ filter: { pageNumber: 1, pageSize: 1000 } }).catch(() => {}),
    ]);
    this.loadingOptions = false;

    if (this.isEdit) {
      const r = await this.getClientGroup({ id: this.$route.params.id });
      const d = r?.data || {};
      this.form.name = d.name || "";
      this.form.description = d.description || "";
      this.form.color = d.color || "#7C4DFF";
      this.form.debtor_codes = d.debtor_codes || [];
      this.form.lead_uuids = d.lead_uuids || [];
      this.form.contact_uuids = d.contact_uuids || [];
      this.form.opportunity_uuids = d.opportunity_uuids || [];
      this.form.user_uuids = d.user_uuids || [];
      this.hydrateSelections();
    }
  },
  methods: {
    ...clientGroupMethods,
    ...clientMethods,
    ...leadMethods,
    ...contactMethods,
    ...opportunityMethods,
    ...usersMethods,
    /** Turns the saved key lists back into the option objects the pickers show. */
    hydrateSelections() {
      const pick = (options, keys) => options.filter((o) => (keys || []).includes(o.key));
      // A saved customer the opening list does not carry gets a placeholder
      // rather than being quietly left out: dropped here, it would disappear
      // from the group the next time anyone touched the picker and saved.
      // Its name is filled in afterwards.
      const found = pick(this.debtorOptions, this.form.debtor_codes);
      const have = new Set(found.map((o) => o.key));
      const missing = (this.form.debtor_codes || []).filter((c) => !have.has(c));
      this.selected.debtors = [...found, ...missing.map((c) => ({ key: c, label: c }))];
      if (missing.length) this.resolveDebtorLabels(missing);
      this.selected.leads = pick(this.leadOptions, this.form.lead_uuids);
      this.selected.contacts = pick(this.contactOptions, this.form.contact_uuids);
      this.selected.opportunities = pick(this.opportunityOptions, this.form.opportunity_uuids);
      this.selected.users = pick(this.userOptions, this.form.user_uuids);
    },
    /**
     * Put names on the placeholders hydrateSelections had to make.
     *
     * One lookup each, so it is bounded: past a couple of dozen the codes stay
     * as they are rather than firing a request storm at the ledger.
     */
    async resolveDebtorLabels(codes) {
      const results = await Promise.all(
        codes.slice(0, 25).map((code) =>
          getDebtorsDirect(code, 5)
            .then((r) => (r?.data || []).find(
              (d) => String(d.account_no || d.accountNo || "").trim().toUpperCase()
                === String(code).trim().toUpperCase()
            ))
            .catch(() => null)
        )
      );

      this.selected.debtors = this.selected.debtors.map((o) => {
        const hit = results.find((d) => d && (d.account_no || d.accountNo) === o.key);
        return hit ? { key: o.key, label: `${o.key} — ${hit.company_name || hit.companyName || ""}` } : o;
      });
    },
    /** Search the ledger's customers as the user types. Debounced. */
    onDebtorSearch(query) {
      clearTimeout(this.debtorSearchTimer);
      const q = (query || "").trim();
      this.debtorSearchTimer = setTimeout(async () => {
        this.debtorSearching = true;
        try {
          const r = await getDebtorsDirect(q || undefined, 50);
          this.debtorSearchResults = r?.data || [];
        } catch (_) {
          // Keep the last good list: an empty picker mid-typing reads as
          // "no such customer".
        } finally {
          this.debtorSearching = false;
        }
      }, 300);
    },
    onMembersChange(sectionKey, field, list) {
      this.selected[sectionKey] = list || [];
      this.form[field] = this.selected[sectionKey].map((o) => o.key);
    },
    pickColor(c) {
      this.form.color = c;
    },
    async submit() {
      const result = await this.submitForm(
        () =>
          this.isEdit
            ? this.updateClientGroup({ id: this.$route.params.id, data: this.form })
            : this.createClientGroup({ data: this.form }),
        // The store already alerts on success for this module.
        { errorContext: () => null }
      );
      if (result) this.$router.push({ name: "Client-group-listing" });
    },
    cancel() {
      this.$router.push({ name: "Client-group-listing" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('label.name') + ' *'">
                    <b-form-input
                      v-model="form.name"
                      :class="{ 'is-invalid': isInvalid('name') }"
                      @input="onFieldInput('name')"
                    />
                    <div v-if="fieldError('name', $t('label.name'))" class="invalid-feedback d-block">
                      {{ fieldError('name', $t('label.name')) }}
                    </div>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('clientGroup.description')">
                    <b-form-textarea v-model="form.description" rows="2" />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('clientGroup.color')">
                    <div class="color-picker">
                      <button
                        v-for="c in presetColors"
                        :key="c"
                        type="button"
                        class="color-swatch"
                        :class="{ active: form.color === c }"
                        :style="{ background: c }"
                        @click="pickColor(c)"
                      ></button>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- What is in this group -->
              <div class="section-head">
                <i class="mdi mdi-folder-multiple-outline mr-1"></i>{{ $t('clientGroup.membersSection') }}
                <span class="section-count">{{ totalRecords }}</span>
              </div>

              <div v-if="loadingOptions" class="text-center py-3">
                <b-spinner small variant="primary"></b-spinner>
              </div>

              <b-row v-else>
                <b-col cols="12" v-for="s in memberSections" :key="s.key">
                  <b-form-group>
                    <template #label>
                      <i class="mdi mr-1" :class="s.icon"></i>{{ s.label }}
                      <span class="pill">{{ form[s.field].length }}</span>
                    </template>
                    <multiselect
                      :value="selected[s.key]"
                      :options="s.options"
                      track-by="key"
                      label="label"
                      :multiple="true"
                      :close-on-select="false"
                      :show-labels="false"
                      open-direction="below"
                      :internal-search="!s.onSearch"
                      :loading="!!s.loading"
                      :placeholder="s.onSearch ? $t('label.searchCustomer') : $t('label.select')"
                      @search-change="s.onSearch"
                      @input="(list) => onMembersChange(s.key, s.field, list)"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Who can see it -->
              <div class="section-head access">
                <i class="mdi mdi-eye-outline mr-1"></i>{{ $t('clientGroup.accessSection') }}
                <span class="section-count">{{ form.user_uuids.length }}</span>
              </div>

              <b-row>
                <b-col cols="12">
                  <b-form-group>
                    <template #label>
                      <i class="mdi mdi-account-tie-outline mr-1"></i>{{ $t('clientGroup.assignedStaff') }}
                    </template>
                    <multiselect
                      :value="selected.users"
                      :options="userOptions"
                      track-by="key"
                      label="label"
                      :multiple="true"
                      :close-on-select="false"
                      :show-labels="false"
                      open-direction="below"
                      :placeholder="$t('label.select')"
                      @input="(list) => onMembersChange('users', 'user_uuids', list)"
                    />
                    <small class="text-muted d-block mt-1">
                      <i class="mdi mdi-information-outline mr-1"></i>{{ $t('clientGroup.accessHelp') }}
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting">{{ $t('label.submit') }}</b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>

      <!-- Live preview -->
      <div class="col-12 col-lg-4">
        <div class="preview-card" :style="{ borderTopColor: form.color }">
          <div class="preview-head">
            <strong>{{ form.name || $t('clientGroup.untitled') }}</strong>
          </div>
          <p class="preview-desc">{{ form.description || $t('clientGroup.noDescription') }}</p>

          <div class="preview-stats">
            <div v-for="s in memberSections" :key="s.key" class="preview-stat">
              <span class="preview-stat-value" :style="{ color: form.color }">{{ form[s.field].length }}</span>
              <span class="preview-stat-label">{{ s.label }}</span>
            </div>
          </div>

          <div class="preview-access">
            <div class="preview-access-title">
              <i class="mdi mdi-eye-outline mr-1"></i>{{ $t('clientGroup.accessSection') }}
            </div>
            <div v-if="!selected.users.length" class="preview-access-empty">
              {{ $t('clientGroup.noStaffYet') }}
            </div>
            <div v-else>
              <span v-for="u in selected.users" :key="u.key" class="staff-chip">{{ u.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.color-picker { display: flex; gap: 8px; flex-wrap: wrap; }
.color-swatch {
  width: 32px; height: 32px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer; padding: 0;
  transition: all 0.15s ease;
}
.color-swatch.active {
  border-color: #212529;
  transform: scale(1.1);
  box-shadow: 0 0 0 2px #fff, 0 4px 10px rgba(0, 0, 0, 0.15);
}

.section-head {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #495057;
  border-top: 1px solid #e9ecef;
  padding-top: 14px;
  margin: 8px 0 14px;
}
.section-head.access { color: #5E2FE0; }
.section-count {
  background: #f1f3f5;
  border-radius: 10px;
  padding: 1px 8px;
  margin-left: 6px;
  font-size: 11px;
}
.pill {
  background: #f1f3f5;
  border-radius: 10px;
  padding: 0 7px;
  margin-left: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #495057;
}

.preview-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-top: 4px solid #7C4DFF;
  border-radius: 12px;
  padding: 16px 18px;
  position: sticky; top: 90px;
}
.preview-head { margin-bottom: 6px; }
.preview-desc { font-size: 12px; color: #8993A4; margin-bottom: 14px; }
.preview-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.preview-stat {
  background: #fafbfc;
  border-radius: 8px;
  padding: 8px 10px;
}
.preview-stat-value { font-size: 20px; font-weight: 700; display: block; line-height: 1.1; }
.preview-stat-label { font-size: 11px; color: #8993A4; }

.preview-access { border-top: 1px dashed #e9ecef; padding-top: 12px; }
.preview-access-title {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.4px; color: #5E2FE0; margin-bottom: 6px;
}
.preview-access-empty { font-size: 12px; color: #adb5bd; }
.staff-chip {
  display: inline-block;
  background: rgba(124, 77, 255, 0.08);
  color: #5E2FE0;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 6px;
  margin: 0 4px 4px 0;
}
</style>
